// frontend/src/utils/kimiOutputFilter.ts

/**
 * Filters Kimi CLI output to show only user-facing messages.
 * Suppresses code lines, file contents, and technical output.
 * Admin users see full unfiltered output.
 */

// Pattern to match numbered code lines like "     1\t<!DOCTYPE"
const CODE_LINE_PATTERN = /^\s*\d+\t/;

// Pattern to match system message tags: <system>...</system>
const SYSTEM_TAG_REGEX = /<system>(.*?)<\/system>/;

// Patterns for direct URLs and markdown links
const URL_PATTERN = /https?:\/\/\S+/i;
const MARKDOWN_LINK_PATTERN = /\[[^\]]+\]\((https?:\/\/[^\s)]+)\)/i;

/**
 * Best-effort detection for raw Kimi stream output persisted to DB.
 * We intentionally keep this strict to avoid filtering normal Claude answers.
 */
export function shouldFilterKimiOutput(content: string): boolean {
  if (!content) return false;
  if (SYSTEM_TAG_REGEX.test(content)) return true;

  const lines = content.split('\n');
  let numberedCodeLines = 0;
  for (const line of lines) {
    if (CODE_LINE_PATTERN.test(line)) {
      numberedCodeLines += 1;
      if (numberedCodeLines >= 2) return true;
    }
  }
  return false;
}

/**
 * Check if a line appears to be code content
 */
function isCodeLine(line: string): boolean {
  const stripped = line.trim();
  if (!stripped) return false;

  // Lines with line numbers (from file reads)
  if (CODE_LINE_PATTERN.test(line)) return true;

  // Common code patterns to filter
  const codeIndicators = [
    stripped.startsWith('import '),
    stripped.startsWith('from '),
    stripped.startsWith('def '),
    stripped.startsWith('class '),
    stripped.startsWith('if __name__'),
    stripped.startsWith('async def '),
    stripped.startsWith('await '),
    stripped.startsWith('return '),
    stripped.startsWith('raise '),
    stripped.startsWith('try:'),
    stripped.startsWith('except'),
    stripped.startsWith('finally:'),
    stripped.startsWith('with '),
    stripped.startsWith('for ') && stripped.includes(':'),
    stripped.startsWith('while ') && stripped.includes(':'),
    stripped.startsWith('@'), // decorators
    stripped.startsWith('#!/'), // shebang
    stripped.startsWith('<!DOCTYPE'),
    stripped.startsWith('<html'),
    stripped.startsWith('<head'),
    stripped.startsWith('<body'),
    stripped.startsWith('<meta'),
    stripped.startsWith('<style'),
    stripped.startsWith('<script'),
    stripped.startsWith('<div'),
    stripped.startsWith('<span'),
    stripped.startsWith('<form'),
    stripped.startsWith('<input'),
    stripped.startsWith('<button'),
    stripped.startsWith('<link'),
    stripped.startsWith('<title'),
    stripped.startsWith('</'), // closing HTML tags
    stripped.startsWith('{') && stripped.endsWith('}'), // JSON/dict
    stripped.startsWith('[') && stripped.endsWith(']'), // lists
    stripped.includes('= {'),
    stripped.includes('= ['),
    stripped.startsWith('const '),
    stripped.startsWith('let '),
    stripped.startsWith('var '),
    stripped.startsWith('function '),
    stripped.startsWith('window.'),
    stripped.startsWith('document.'),
    stripped.endsWith(';') && !stripped.startsWith('✓'),
    stripped.endsWith(',') && stripped.length < 80 && !stripped.includes('('),
    stripped.startsWith('RUN '), // Dockerfile
    stripped.startsWith('FROM '),
    stripped.startsWith('COPY '),
    stripped.startsWith('ENV '),
    stripped.startsWith('CMD '),
    stripped.startsWith('WORKDIR '),
    // Python docstrings
    stripped.startsWith("'''") || stripped.startsWith('"""'),
    stripped.endsWith("'''") || stripped.endsWith('"""'),
    // CSS
    stripped.includes('{') && stripped.includes(':') && stripped.includes(';'),
    /^\s*\.\w+/.test(stripped), // CSS class selectors
    /^\s*#\w+\s*\{/.test(stripped), // CSS id selectors
  ];

  return codeIndicators.some(Boolean);
}

/**
 * Check if a line should be considered for keeping (before processing)
 */
function shouldConsiderLine(line: string): boolean {
  const stripped = line.trim();
  
  // Skip completely empty lines initially (we manage spacing separately)
  if (!stripped) return false;

  // Lines with system tags need special processing
  if (line.includes('<system>')) return true;

  // Keep direct URLs and markdown links
  if (URL_PATTERN.test(stripped) || MARKDOWN_LINK_PATTERN.test(stripped)) return true;

  // Keep markdown headers (but not CSS comments that start with /*)
  if (stripped.startsWith('#') && stripped.includes(' ') && !stripped.startsWith('#!')) return true;

  // Keep numbered list items
  if (/^\d+\.\s/.test(stripped)) return true;

  // Keep list items that are narrative (not code)
  if (stripped.startsWith('- ') || stripped.startsWith('* ')) {
    const rest = stripped.slice(2).trim();
    // Filter out code-like list items
    if (rest.startsWith('`') || rest.slice(0, 20).includes('=')) return false;
    if (rest.startsWith('<') || rest.startsWith('{')) return false;
    if (rest.startsWith('import ') || rest.startsWith('from ')) return false;
    return true;
  }

  // Keep test output
  if (stripped.startsWith('✓') || stripped.startsWith('✗')) return true;
  if (stripped.startsWith('Testing ') || stripped.startsWith('🎉')) return true;

  // Keep emoji-prefixed status lines
  if (/^[🔧✅❌⚠️📝🚀💡📦🔍🎯📊💾🔄⏳✨🛠️]/.test(stripped)) return true;

  // Keep narrative sentences (has spaces, starts with capital letter, long enough)
  if (stripped.includes(' ') && stripped.length > 25) {
    const firstChar = stripped[0];
    // Check if starts with capital letter (not a symbol)
    if ((firstChar >= 'A' && firstChar <= 'Z') || stripped.startsWith("I'") || stripped.startsWith("I ")) {
      if (!isCodeLine(line)) return true;
    }
  }

  // Keep summary/status messages
  const summaryPatterns = [
    'Summary', 'Created', 'Modified', 'Updated', 'Deleted',
    'successfully', 'complete', 'finished', 'done', 'passed',
    'Features', 'Changes', 'Result', 'Ready',
    'Now ', 'Let me', "I'll ", "I've ", "Here's", 'Perfect',
    'Fixed', 'Added', 'Removed', 'Replaced', 'Kept',
    'The changes', 'This will', 'This should',
  ];
  
  if (summaryPatterns.some(p => stripped.includes(p))) {
    if (!isCodeLine(line)) return true;
  }

  return false;
}

/**
 * Process a line - extract system message if present, strip trailing code
 */
function processLine(line: string): string | null {
  const stripped = line.trim();
  if (!stripped) return null;

  // If line has system tag, extract just the message
  const systemMatch = line.match(SYSTEM_TAG_REGEX);
  if (systemMatch) {
    const message = systemMatch[1].trim();
    // Format as a subtle status message
    return `📄 ${message}`;
  }

  // For other lines, return as-is if they pass the filter
  if (shouldConsiderLine(line) && !isCodeLine(line)) {
    return line;
  }
  
  return null;
}

/**
 * Filter Kimi output to show only user-facing messages.
 * 
 * @param content - Raw Kimi output content
 * @param isAdmin - If true, returns unfiltered content
 * @param isKimiModel - If false, returns unfiltered content (only filter Kimi output)
 * @returns Filtered content for display
 */
export function filterKimiOutput(
  content: string,
  isAdmin: boolean,
  isKimiModel: boolean
): string {
  // Admin sees everything
  if (isAdmin) return content;
  
  // Only filter Kimi model output
  if (!isKimiModel) return content;

  const lines = content.split('\n');
  const filteredLines: string[] = [];
  let consecutiveBlankCount = 0;

  for (const line of lines) {
    const processed = processLine(line);
    if (processed !== null) {
      // Manage spacing - allow max 1 blank line between content
      if (processed.trim() === '') {
        if (consecutiveBlankCount < 1 && filteredLines.length > 0) {
          filteredLines.push('');
          consecutiveBlankCount++;
        }
      } else {
        filteredLines.push(processed);
        consecutiveBlankCount = 0;
      }
    }
  }

  // Clean up trailing blank lines
  while (filteredLines.length > 0 && filteredLines[filteredLines.length - 1] === '') {
    filteredLines.pop();
  }

  return filteredLines.join('\n');
}

/**
 * Incrementally filter new content chunk.
 * Useful for streaming where we process chunks as they arrive.
 */
export class KimiOutputFilterStream {
  private buffer = '';
  private isAdmin: boolean;
  private isKimiModel: boolean;
  private lastLineWasBlank = false;

  constructor(isAdmin: boolean, isKimiModel: boolean) {
    this.isAdmin = isAdmin;
    this.isKimiModel = isKimiModel;
  }

  /**
   * Process a new chunk of content
   * @returns The filtered content to display
   */
  process(chunk: string): string {
    // Admin sees everything, non-Kimi models unfiltered
    if (this.isAdmin || !this.isKimiModel) {
      return chunk;
    }

    this.buffer += chunk;
    
    // Process complete lines
    const lines = this.buffer.split('\n');
    
    // Keep incomplete last line in buffer
    if (!this.buffer.endsWith('\n')) {
      this.buffer = lines.pop() || '';
    } else {
      this.buffer = '';
    }

    const filteredLines: string[] = [];
    for (const line of lines) {
      const processed = processLine(line);
      if (processed !== null) {
        // Avoid multiple consecutive blank lines
        const isBlank = processed.trim() === '';
        if (isBlank) {
          if (!this.lastLineWasBlank && filteredLines.length > 0) {
            filteredLines.push('');
            this.lastLineWasBlank = true;
          }
        } else {
          filteredLines.push(processed);
          this.lastLineWasBlank = false;
        }
      }
    }

    if (filteredLines.length > 0) {
      return filteredLines.join('\n') + '\n';
    }
    return '';
  }

  /**
   * Flush any remaining buffered content
   */
  flush(): string {
    if (this.isAdmin || !this.isKimiModel) {
      const remaining = this.buffer;
      this.buffer = '';
      return remaining;
    }

    if (this.buffer) {
      const line = this.buffer;
      this.buffer = '';
      const processed = processLine(line);
      if (processed !== null) {
        return processed;
      }
    }
    return '';
  }
}
